/**
 * MI BOLETÍN de una cohorte (E2) — la pestaña «Notas» del expediente.
 *
 * NO reutiliza `assessment/grades-tab.tsx`: ese es el gradebook del PROFESOR y
 * pide `GET /api/cohorts/:id/gradebook`, la matriz de TODO el roster. Un alumno
 * no debe verla —ni el endpoint se la daría— pero sí debe ver su propia fila
 * con los pesos, y eso ya está en su dispositivo: `grade_category` y
 * `grade_item` sincronizan a la cohorte entera (transparencia de pesos) y
 * `assignment_grade` solo trae sus filas.
 *
 * La ponderación la hace `buildReportCard` (@space/shared), espejo puro del
 * `weighted_final` de Rust. Además de la nota oficial muestra la PROYECTADA —
 * la de ponderar solo lo ya calificado — porque a mitad de curso la oficial
 * cuenta como 0 todo lo pendiente y no informa de nada.
 */
import { type Component, createMemo, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { resolveGradingScheme } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useOrgSettings } from "../../hooks/use-academy-data";
import { useCourseSettings } from "../../hooks/use-course-modules";
import { useCohort } from "../../hooks/use-cohort-data";
import { useMyEnrollment, useMyReportCard } from "../../hooks/use-student-record";

const pct = (raw: number) => Math.round(raw * 1000) / 10;

export const CohortReportCard: Component<{ cohortId: string; cohortName: string }> = (props) => {
  const { t } = useI18n();
  const cohortId = () => props.cohortId;
  const cohort = useCohort(cohortId);
  const courseId = () => cohort.data?.course_id ?? undefined;
  const orgId = () => cohort.data?.organization_id ?? undefined;
  const courseSettings = useCourseSettings(courseId);
  const orgSettings = useOrgSettings(orgId);
  const enrollment = useMyEnrollment(cohortId);
  const card = useMyReportCard(cohortId, courseId);

  // Precedencia cohorte → curso → org → defecto, la misma que resuelve el
  // servidor; el registro de escalas es un espejo display-only.
  const scheme = createMemo(() =>
    resolveGradingScheme(
      cohort.data?.grading_scheme,
      courseSettings.data?.grading_scheme,
      orgSettings.data?.grading_scheme,
    ),
  );

  const sealed = () => enrollment.row()?.finalGrade ?? null;
  const hasItems = () => card().cells.length > 0;

  return (
    <section class="space-y-3 rounded-2xl border border-border p-4" data-testid="report-card">
      <h3 class="font-display text-base font-bold">{props.cohortName}</h3>

      <Show
        when={hasItems()}
        fallback={
          <p class="text-sm text-muted-foreground" data-testid="report-card-empty">
            {t(msg`Your teacher hasn't set up the gradebook for this cohort yet.`)}
          </p>
        }
      >
        {/* Resumen: proyectada + oficial (o la final sellada al cierre). */}
        <div class="grid gap-2 sm:grid-cols-2">
          <div class="rounded-xl bg-accent/10 p-3" data-testid="report-card-projected">
            <p class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t(msg`Projected grade`)}
            </p>
            <p class="font-display text-2xl font-extrabold text-accent">
              {scheme().toDisplay(card().projectedRaw)}
            </p>
            <p class="text-xs text-muted-foreground">
              {t(msg`Weighing only what has been graded`)} · {card().gradedCount}/
              {card().gradableCount}
            </p>
          </div>
          <div class="rounded-xl bg-muted/40 p-3" data-testid="report-card-official">
            <p class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Show when={sealed() !== null} fallback={t(msg`Grade so far`)}>
                {t(msg`Final grade`)}
              </Show>
            </p>
            <p class="font-display text-2xl font-extrabold">
              <Show
                when={sealed() !== null}
                fallback={<>{scheme().displayValue(scheme().convert(card().officialRaw))}</>}
              >
                {scheme().displayValue(sealed()!)}
              </Show>
            </p>
            <p class="text-xs text-muted-foreground">
              <Show
                when={sealed() !== null}
                fallback={t(msg`Anything not graded yet counts as zero`)}
              >
                {t(msg`Sealed when the cohort closed`)}
              </Show>
            </p>
          </div>
        </div>

        {/* Desglose por categoría, con el peso a la vista. */}
        <For each={card().categories}>
          {(group) => (
            <div class="space-y-1" data-testid="report-card-category">
              <p class="flex items-baseline justify-between text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <span>{group.name || t(msg`Ungrouped`)}</span>
                <span data-testid="report-card-weight">
                  {t(msg`Weight`)} {group.weight}
                  <Show when={group.dropLowest > 0}>
                    {" · "}
                    {t(msg`drops lowest`)} {group.dropLowest}
                  </Show>
                </span>
              </p>
              <ul class="space-y-1">
                <For each={group.cells}>
                  {(cell) => (
                    <li
                      class="flex items-center justify-between rounded-lg border border-border px-3 py-1.5 text-sm"
                      data-testid="report-card-item"
                      data-graded={cell.graded ? "true" : "false"}
                    >
                      <span class="min-w-0 truncate">{cell.name}</span>
                      <span class="ms-2 shrink-0 font-semibold">
                        <Show
                          when={cell.excused}
                          fallback={
                            <Show
                              when={cell.graded}
                              fallback={
                                <span class="text-xs font-normal text-muted-foreground">
                                  {t(msg`Pending`)}
                                </span>
                              }
                            >
                              {Math.round((cell.score ?? 0) * 10) / 10} / {cell.maxScore}
                            </Show>
                          }
                        >
                          <span class="text-xs font-normal text-muted-foreground">
                            {t(msg`Excused`)}
                          </span>
                        </Show>
                      </span>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          )}
        </For>

        <p class="text-xs text-muted-foreground" data-testid="report-card-raw">
          {t(msg`Projected`)} {pct(card().projectedRaw)}% · {t(msg`So far`)}{" "}
          {pct(card().officialRaw)}%
        </p>
      </Show>
    </section>
  );
};
